import { PureComponent, type ReactNode } from "react";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import { cn } from "@btnet/utilities-core";

import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { MarkupField } from "commonComponents/financial/MarkupField/MarkupField";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IBaseLineItem,
    type IMarkupLineItem,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { LineItemColumns } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import { type TaxMethod } from "entity/tax/common/tax.types";

export interface IMarkupCellProps extends IMarkupLineItem, IPreviewLineItemProps {
    totalEffectiveRate?: number;
    taxMethod?: TaxMethod;
    debouncingContext?: IInputDebouncingContext;
    numMarkupSources?: number;
    popoverContent?: ReactNode;
}

export class MarkupCell extends PureComponent<IGridCellRenderProps<IMarkupCellProps>> {
    private handleBlur = () => {
        const { setRowTouched, rowIndex, field } = this.props;

        // need to set the client price field as touched as well
        setRowTouched!(rowIndex!, [field.name, "ownerPrice"], true);
    };

    render() {
        const { cellValue, field, isInEdit, onChange, canEdit, autoFocus, isDisabled } = this.props;

        if (!cellValue) {
            return <ValueDisplay value={undefined} />;
        }
        const readOnly = !isInEdit || !canEdit;
        const numMarkupSources = cellValue.numMarkupSources;
        const highlightUpdates = cellValue.id <= 0 && !!numMarkupSources;

        const markupFieldComponent = (
            <LineItemPreview
                fieldName="markup"
                addPreviewListener={cellValue.addPreviewListener}
                render={(preview) => (
                    <MarkupField
                        values={{
                            ...cellValue,
                            markupType: preview?.markupType ?? cellValue.markupType,
                            markupPerUnit: preview?.markupPerUnit ?? cellValue.markupPerUnit,
                            markupAmount: preview?.markupAmount ?? cellValue.markupAmount,
                            markupPercent: preview?.markupPercentage ?? cellValue.markupPercent,
                        }}
                        setFieldTouched={this.handleBlur}
                        setValues={(values) => {
                            onChange(field.path, values);
                        }}
                        readOnly={readOnly}
                        isDisabled={isDisabled}
                        autoFocus={autoFocus}
                        onFocus={this.props.onFocus}
                        data-testid={`${field.path}.markupField`}
                        canEdit={canEdit}
                        className={preview ? "LineItemPreview" : undefined}
                        debouncingContext={cellValue.debouncingContext}
                        highlightUpdates={highlightUpdates}
                        highlightWarning={highlightUpdates && numMarkupSources > 1}
                    />
                )}
            />
        );

        if (isInEdit) {
            const showTooltip =
                (highlightUpdates &&
                    cellValue.popoverContent &&
                    cellValue.markupType === MarkupColumnTypes.Percentage &&
                    cellValue.markupPercent > 0) ||
                (cellValue.markupType === MarkupColumnTypes.Margin && (cellValue.margin ?? 0) > 0);

            const markupLabel = (
                <BTPopover
                    content={cellValue.popoverContent}
                    disabled={!showTooltip}
                    data-testid="markup-source-tooltip"
                >
                    <span
                        className={cn({
                            "dotted-underline": showTooltip,
                        })}
                    >
                        Markup
                    </span>
                </BTPopover>
            );
            return (
                <BTFormItem
                    label={markupLabel}
                    className={cn(
                        "LineItemCell MarkupCell",
                        { RightAlignFormLabel: !canEdit },

                        { ellipsis: cellValue?.ellipsis },
                    )}
                >
                    {markupFieldComponent}
                </BTFormItem>
            );
        }

        return (
            <div
                className={cn(
                    "LineItemCell",
                    { lineItemEditableCell: canEdit && !isInEdit },
                    { ellipsis: cellValue?.ellipsis },
                )}
            >
                {markupFieldComponent}
            </div>
        );
    }
}

export const getMarkupCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.Markup,
        name: "Markup",
        jsonKey: "markupFields",
        width: 140.0,
        minWidth: 48,
        ellipsis: true,
        align: "right",
        isSortable: true,
        columnType: GridLineItemColumnType.markup,
        isFrozen: false,
        isHidden: false,
        Cell: MarkupCell,
    }) as Partial<ILineItemColumn<T, IMarkupLineItem>>;
